import { get, omit, omitBy, matches, filter, includes, isEmpty, split, reduce, chain, concat } from "lodash"
import { createReducer } from "reduxsauce"
import createActions from "helpers/actionCreator"
import { Types as MarketToolsTypes } from "modules/market-tools/model"
import { computeUpdatedFilters } from "helpers/filters"
import { getQueryStringSelectedColumns } from "./selectors"

export const { Types, Creators } = createActions("ompnt/modules/market-tools-securities-finder", {
  setPage: ["page"],
  setFilter: ["filter"],
  updateFilters: {
    props: ["filter", "context"],
    track: true,
    trackType: ({ context }) => {
      const prefix = context ? `${context}-` : ""
      return `${prefix}market-screener-filter-selection`
    },
    trackPayload: ({ filter }) => ({ filter }),
  },
  setDate: ["date"],
  setSort: ["sort"],
  toggleFacet: ["facetType", "id"],
  toggleClassificationFacets: ["facetType", "tier", "ids", "selected"],
  setSelectedColumns: {
    props: ["columns", "modelId", "accountId"],
    updatePreference: {
      getItems: (_, { accountId, modelId }) => [{
        type: "securityFinderSelectedColumns",
        value: getQueryStringSelectedColumns(),
        key: modelId,
        updateCollection: {
          resource: "securityFinderPreferences",
          id: `securityFinderPreferences-${accountId}${modelId}`,
        },
      }],
    },
  },
})

const DEFAULT_STATE = {
  editing: null,
}

function handleUpdateFilters(state, { filter }) {
  return { ...state, ...computeUpdatedFilters(filter) }
}

export default createReducer(DEFAULT_STATE, {
  [Types.UPDATE_FILTERS]: handleUpdateFilters,
})

function handleSetDate(state, { date }) {
  return {
    ...state,
    date,
  }
}

function handleUpdateFiltersQS(state, { filter }) {
  const { filter: updatedFilter } = computeUpdatedFilters(filter)
  return { ...state, filter: updatedFilter }
}

function handleSetFilter(state, { filter }) {
  return { ...state, filter }
}

const DEFAULT_SORT = { type: "descriptors", id: "averageDailyVolume", direction: "desc" }

function handleSetSort(state, { sort }) {
  const isDefault = matches(DEFAULT_SORT)(omit(sort, "getter"))
  return { ...state, sort: isDefault ? null : sort }
}

function handleSetPage(state, { page }) {
  return { ...state, page: page === 0 ? null : page }
}

function handleToggleFacet(state, { facetType, id }) {
  const toggled = includes(state[facetType], id, false)
  const currentSelectedFacets = state[facetType] || []
  if (!toggled) {
    return { ...state, [facetType]: [...currentSelectedFacets, id] }
  } else {
    return { ...state, [facetType]: filter(currentSelectedFacets, f => f !== id) }
  }
}

function handleToggleClassificationFacets(state, { facetType, tier, ids, selected }) {
  /** Concatenates or removes facet IDs based on the `selected` flag */
  function mergeFacetIds(objValue, srcValue) {
    if (Array.isArray(objValue)) {
      return selected ? concat(objValue, srcValue) : filter(objValue, v => !includes(srcValue, v))
    }
  }

  const originalFacets = get(state, "classifications", {})
  const newSelectedFacets = {
    [facetType]: {
      [tier]: ids,
    },
  }

  const merged = chain({})
    .mergeWith(originalFacets, newSelectedFacets, mergeFacetIds)
    .reduce((filtered, tier, tierId) => {
      const filteredTier = omitBy(tier, facet => isEmpty(facet))
      return {
        ...filtered,
        ...(!isEmpty(filteredTier) && { [tierId]: filteredTier }),
      }
    }, {})
    .value()

  if (isEmpty(merged)) {
    return omit(state, ["classifications"])
  }

  return {
    ...state,
    classifications: {
      ...merged,
    },
  }
}

function handleSetSelectedColumns(state, { columns }) {
  const columnState = reduce(columns, (allColumns, _, column) => {
    const [columnType, columnMetric, columnMetricType] = split(column, ".")
    const columnKey = columnMetricType ? `${columnMetric}.${columnMetricType}` : columnMetric
    return {
      ...allColumns,
      [columnType]: [...get(allColumns, columnType, []), columnKey],
    }
  }, {})
  return { ...state, columns: columnState }
}

function handleSetModel(state) {
  return omit(state, ["sort", "columns", "filter"])
}

export const queryStringReducers = {
  [Types.SET_DATE]: handleSetDate,
  [Types.SET_FILTER]: handleSetFilter,
  [Types.SET_SORT]: handleSetSort,
  [Types.SET_PAGE]: handleSetPage,
  [Types.TOGGLE_FACET]: handleToggleFacet,
  [Types.TOGGLE_CLASSIFICATION_FACETS]: handleToggleClassificationFacets,
  [Types.SET_SELECTED_COLUMNS]: handleSetSelectedColumns,
  [MarketToolsTypes.SET_MODEL]: handleSetModel,
  [Types.UPDATE_FILTERS]: handleUpdateFiltersQS,
}
